/**
=========================================================
* Complete registration after sign-up (Name, Email, Password)
* Uses new-user components: UserInfo, BirthData, Profile
=========================================================
*/

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Formik, Form } from "formik";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import UserInfo from "layouts/pages/users/new-user/components/UserInfo";
import BirthData from "layouts/pages/users/new-user/components/BirthData";
import Profile from "layouts/pages/users/new-user/components/Profile";

import validations from "layouts/pages/users/new-user/schemas/validations";
import form from "layouts/pages/users/new-user/schemas/form";
import initialValues from "layouts/pages/users/new-user/schemas/initialValues";

import { useAuth } from "context/AuthContext";

import rocket from "assets/images/illustrations/rocket-white.png";

function getSteps() {
  return ["User Info", "Birth Data", "Profile"];
}

function getStepContent(stepIndex, formData) {
  switch (stepIndex) {
    case 0:
      return <UserInfo formData={formData} />;
    case 1:
      return <BirthData formData={formData} />;
    case 2:
      return <Profile formData={formData} />;
    default:
      return null;
  }
}

function SignUpComplete() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeStep, setActiveStep] = useState(0);

  const registration = location.state?.registration;
  const prefill = registration
    ? {
        ...initialValues,
        firstName: registration.firstName || "",
        lastName: registration.lastName || "",
        email: registration.email || "",
        password: registration.password || "",
      }
    : null;

  useEffect(() => {
    if (!registration) {
      navigate("/sign-up", { replace: true });
    }
  }, [registration, navigate]);

  const steps = getSteps();
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const handleBack = () => setActiveStep(activeStep - 1);

  const submitForm = async (values, actions) => {
    try {
      await login(values.email, values.password);
      navigate("/dashboards/default", { replace: true });
    } catch {
      navigate("/sign-in", { replace: true });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  if (!prefill) {
    return null;
  }

  return (
    <IllustrationLayout
      title="Complete registration"
      description="Birth data and profile (User Info is pre-filled)"
      showNavbar={false}
      illustration={{
        image: rocket,
        title: "Your journey starts here",
        description:
          "Just as it takes a company to sustain a product, it takes a community to sustain a protocol.",
      }}
    >
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Formik
        initialValues={prefill}
        validationSchema={currentValidation}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form id={formId} autoComplete="off">
            <Card sx={{ overflow: "visible" }}>
              <SoftBox p={2}>
                {getStepContent(activeStep, {
                  values,
                  touched,
                  formField,
                  errors,
                })}
                <SoftBox mt={2} width="100%" display="flex" justifyContent="space-between">
                  {activeStep === 0 ? (
                    <SoftBox />
                  ) : (
                    <SoftButton variant="gradient" color="light" type="button" onClick={handleBack}>
                      back
                    </SoftButton>
                  )}
                  <SoftButton
                    disabled={isSubmitting}
                    type="submit"
                    variant="gradient"
                    color="primary"
                  >
                    {isLastStep ? "Complete" : "next"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Form>
        )}
      </Formik>
    </IllustrationLayout>
  );
}

export default SignUpComplete;
